import type { AdjustmentOptions } from './filters';
import { applyAdjustmentsToCanvas } from './filters';
import type { BackgroundRemovalOptions } from './backgroundRemovalService';
import {
  isPngOrJpeg,
  removeBackgroundFromBlob,
  toPngBlob,
} from './backgroundRemovalService';
import { resizeCanvas } from './resizeService';
import {
  clampIconQrSizeToCanvas,
  clampWatermarkFontSizeToCanvas,
} from './overlayLimits';

export interface OutputFormatOption {
  readonly id: string;
  readonly label: string;
  readonly mime: string;
  readonly ext: string;
  readonly quality?: number;
}

export interface AspectRatioOption {
  readonly id: string;
  readonly label: string;
  readonly ratio: number | null;
}

export interface OutputSizeOption {
  /** 目標寬度（px），留空則依高度與原始比例推算或沿用原圖寬度 */
  readonly width?: number;
  /** 目標高度（px），留空則依寬度與原始比例推算或沿用原圖高度 */
  readonly height?: number;
  /**
   * 是否允許在目標尺寸大於圖片時「不放大內容，只在外圍留白」。
   * 開啟後會以原圖尺寸為基準置中繪製，其餘區域以 backgroundColor 或透明填滿。
   */
  readonly allowPadding?: boolean;
  /** 邊框／留白區域背景色（CSS color 或 #RRGGBB），未指定時為透明背景。與 backgroundImage 二選一。 */
  readonly backgroundColor?: string;
  /** 背景圖（data URL），填滿畫布（cover）；與 backgroundColor 二選一。 */
  readonly backgroundImage?: string;
  /**
   * 當啟用 allowPadding 時，控制圖片在畫布中的位置。
   * 0 表示靠左／靠上，0.5 置中，1 靠右／靠下。
   */
  readonly anchorX?: number;
  readonly anchorY?: number;
}

export type PipelineStepId =
  | 'removeBackground'
  | 'aspectRatio'
  | 'upscale'
  | 'resize'
  | 'filters'
  | 'border'
  | 'icons'
  | 'watermark'
  | 'qrcode'
  | 'format';

/** 圖示外框形狀：無／圓形／方形／圓角 */
export type IconShape = 'none' | 'circle' | 'square' | 'rounded';

/** 單一圖示：Lucide、Simple Icons、塗鴉或上傳圖片，可依 anchor 定位（0–1） */
export interface IconItem {
  readonly source: 'lucide' | 'simple' | 'custom' | 'doodle';
  /** Lucide/Simple 時為圖示 id 或 slug；doodle 時為 SVG URL；custom 時可為空 */
  readonly iconId?: string;
  /** 僅 source === 'custom' 時使用，為圖片的 data URL */
  readonly imageDataUrl?: string;
  readonly size: number;
  readonly color: string;
  readonly opacity: number;
  readonly anchorX?: number;
  readonly anchorY?: number;
  /** 圖示外框形狀，預設 none 不裁切 */
  readonly shape?: IconShape;
}

/** 圖示層：多個圖示，在畫布尺寸之後、文字浮水印之前繪製 */
export interface IconsOptions {
  readonly items: readonly IconItem[];
  readonly padding: number;
}

/** 單一文字塊，可依 anchor 自訂位置（0–1，0.5 為置中） */
export interface WatermarkItem {
  readonly text: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly color: string;
  readonly opacity: number;
  /** 水平錨點 0–1，未指定為 0.5 置中 */
  readonly anchorX?: number;
  /** 垂直錨點 0–1，未指定為 0.5 置中 */
  readonly anchorY?: number;
}

/** 文字／浮水印：多個文字塊，在畫布尺寸之後、格式輸出之前繪製 */
export interface WatermarkOptions {
  readonly items: readonly WatermarkItem[];
  /** 距邊緣內距（px） */
  readonly padding: number;
}

/** 單一 QR Code 項目 */
export interface QRCodeItem {
  /** 編碼內容（URL 或文字） */
  readonly content: string;
  /** 單邊尺寸（px） */
  readonly size: number;
  /** 水平錨點 0–1，0.5 置中 */
  readonly anchorX?: number;
  /** 垂直錨點 0–1，0.5 置中 */
  readonly anchorY?: number;
  /** 碼色（前景），CSS 顏色，預設 #000000 */
  readonly darkColor?: string;
  /** 背景色，CSS 顏色，預設 #ffffff */
  readonly lightColor?: string;
}

/** QR Code 層：多個 QR，在 watermark 之後、format 之前繪製 */
export interface QRCodeOptions {
  readonly items: readonly QRCodeItem[];
}

/** 圖片邊線處理：導圓角／形狀遮罩／邊線粗細與顏色 */
export interface BorderOptions {
  /** 形狀：rect 無、rounded 圓角矩形、circle 圓形 */
  readonly shape: 'rect' | 'rounded' | 'circle';
  /** 圓角半徑（px），shape 為 rounded 時使用；0 表示直角 */
  readonly cornerRadius: number;
  /** 邊線粗細（px），0 表示無邊線 */
  readonly borderWidth: number;
  /** 邊線顏色（CSS color） */
  readonly borderColor: string;
}

/** 正規化裁切區（0–1），與預覽裁切框一致時輸出即與預覽一致 */
export interface NormalizedCropRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface PipelineOptions {
  readonly enableRemoveBackground: boolean;
  readonly removeBackgroundOptions?: BackgroundRemovalOptions;
  /** 手動裁切區（與預覽一致）；有值時優先於 aspectRatio */
  readonly cropRect?: NormalizedCropRect | null;
  readonly aspectRatio?: number | null;
  readonly useSmartCrop?: boolean;
  readonly enableUpscale: boolean;
  readonly upscaleFactor: 1 | 2 | 4;
  readonly outputSize?: OutputSizeOption;
  /** 輸出尺寸比例 0.1–1，套用在最終寬高上，1 為不縮小；與 targetOutputPixels 二選一 */
  readonly outputScaleRatio?: number;
  /** 手動指定輸出寬高（px），有值時忽略 outputScaleRatio，可超出比例選項 */
  readonly targetOutputPixels?: { width: number; height: number };
  readonly adjustments?: AdjustmentOptions;
  /** 圖片邊線處理（導圓角、形狀遮罩、邊線），在 filters 之後、resize 之前 */
  readonly border?: BorderOptions | null;
  /** 圖示層（Lucide／品牌 Logo），在畫布尺寸之後、文字浮水印之前 */
  readonly icons?: IconsOptions | null;
  /** 文字／浮水印（多個文字塊），在 icons 之後、format 之前 */
  readonly watermark?: WatermarkOptions | null;
  /** QR Code，在 watermark 之後、format 之前 */
  readonly qrcode?: QRCodeOptions | null;
  readonly format: OutputFormatOption;
}

export interface PipelineProgress {
  readonly currentStep: PipelineStepId | null;
  readonly completedSteps: readonly PipelineStepId[];
}

export interface PipelineResult {
  readonly blob: Blob;
  readonly progress: PipelineProgress;
}

export type PipelineProgressListener = (progress: PipelineProgress) => void;

const MIN_PX = 1;
const MAX_PX = 8000;

/**
 * 依選填的寬高像素計算輸出尺寸：僅寬、僅高、或兩者（塞入框內並維持比例）。
 */
export function getOutputSize(
  cropWidth: number,
  cropHeight: number,
  wantWidth: number,
  wantHeight: number
): [number, number] {
  if (wantWidth < MIN_PX && wantHeight < MIN_PX) {
    if (cropWidth <= MAX_PX && cropHeight <= MAX_PX)
      return [cropWidth, cropHeight];
    const scale = MAX_PX / Math.max(cropWidth, cropHeight);
    return [Math.round(cropWidth * scale), Math.round(cropHeight * scale)];
  }

  const hasWidth = wantWidth >= MIN_PX && wantWidth <= MAX_PX;
  const hasHeight = wantHeight >= MIN_PX && wantHeight <= MAX_PX;

  if (hasWidth && hasHeight) {
    const scale = Math.min(wantWidth / cropWidth, wantHeight / cropHeight);
    return [Math.round(cropWidth * scale), Math.round(cropHeight * scale)];
  }

  if (hasWidth) {
    return [wantWidth, Math.round((wantWidth / cropWidth) * cropHeight)];
  }

  if (hasHeight) {
    return [Math.round((wantHeight / cropHeight) * cropWidth), wantHeight];
  }

  return [cropWidth, cropHeight];
}

/**
 * 裁切框為 normalized 座標時，依「寬／高（像素比）」由寬度推算高度。
 * 使 (widthNorm×imageWidth) / (heightNorm×imageHeight) === widthOverHeightPx。
 */
export function normalizedCropHeightForWidthHeightRatio(
  widthNormalized: number,
  widthOverHeightPx: number,
  imageWidth: number,
  imageHeight: number
): number {
  if (widthOverHeightPx <= 0 || imageWidth < MIN_PX || imageHeight < MIN_PX) {
    return widthNormalized;
  }
  return (widthNormalized * imageWidth) / (widthOverHeightPx * imageHeight);
}

/**
 * 由 normalized 高度與「寬／高（像素比）」推算 normalized 寬度（與 {@link normalizedCropHeightForWidthHeightRatio} 互為反算）。
 */
export function normalizedCropWidthForWidthHeightRatio(
  heightNormalized: number,
  widthOverHeightPx: number,
  imageWidth: number,
  imageHeight: number
): number {
  if (widthOverHeightPx <= 0 || imageWidth < MIN_PX || imageHeight < MIN_PX) {
    return heightNormalized;
  }
  return (heightNormalized * imageHeight * widthOverHeightPx) / imageWidth;
}

/**
 * 輸出畫布上「圖層」的邏輯像素尺寸：圖同時不超過畫布寬高時維持原圖 1:1，否則等比例縮小以完整塞入畫布（與預覽／allowPadding 繪製一致）。
 *
 * @example
 * // 畫布大於圖：不放大，維持原圖像素
 * getCanvasLogicalContentSize(1481, 862, 1536, 960) // { width: 1481, height: 862 }
 * @example
 * // 畫布較小：等比例縮入
 * getCanvasLogicalContentSize(1481, 862, 1000, 1000) // 寬高皆 ≤1000 且維持比例
 */
export function getCanvasLogicalContentSize(
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number
): { width: number; height: number } {
  if (
    imageWidth < MIN_PX ||
    imageHeight < MIN_PX ||
    canvasWidth < MIN_PX ||
    canvasHeight < MIN_PX
  ) {
    return {
      width: Math.max(MIN_PX, Math.round(imageWidth)),
      height: Math.max(MIN_PX, Math.round(imageHeight)),
    };
  }
  if (imageWidth <= canvasWidth && imageHeight <= canvasHeight) {
    return { width: imageWidth, height: imageHeight };
  }
  const [cw, ch] = getOutputSize(
    imageWidth,
    imageHeight,
    canvasWidth,
    canvasHeight
  );
  return { width: cw, height: ch };
}

/**
 * 依目標長寬比計算從原圖裁切的區域（置中），回傳 [sx, sy, sw, sh]。
 */
export function getCropRegion(
  imageWidth: number,
  imageHeight: number,
  targetRatio: number
): [number, number, number, number] {
  const sourceWidth = imageWidth;
  const sourceHeight = imageHeight;
  const ratio = targetRatio;

  if (sourceWidth / sourceHeight > ratio) {
    const cropWidth = sourceHeight * ratio;
    const offsetX = (sourceWidth - cropWidth) / 2;
    return [offsetX, 0, cropWidth, sourceHeight];
  }

  const cropHeight = sourceWidth / ratio;
  const offsetY = (sourceHeight - cropHeight) / 2;
  return [0, offsetY, sourceWidth, cropHeight];
}

export async function loadBlobToCanvas(blob: Blob): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(blob);

  try {
    const image = new Image();

    const loadedImage: HTMLImageElement = await new Promise(
      (resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('圖片載入失敗'));
        image.src = url;
      }
    );

    const canvas = document.createElement('canvas');
    canvas.width = loadedImage.naturalWidth;
    canvas.height = loadedImage.naturalHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('無法建立畫布');
    }

    context.drawImage(loadedImage, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

const MIN_CROP_PX = 1;

/**
 * 依正規化區塊 (0–1) 裁切畫布，使輸出與預覽裁切框一致。
 */
function applyNormalizedRectCrop(
  sourceCanvas: HTMLCanvasElement,
  rect: NormalizedCropRect
): HTMLCanvasElement {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  const startX = Math.round(rect.x * w);
  const startY = Math.round(rect.y * h);
  const cropW = Math.round(rect.width * w);
  const cropH = Math.round(rect.height * h);
  if (
    cropW < MIN_CROP_PX ||
    cropH < MIN_CROP_PX ||
    startX < 0 ||
    startY < 0 ||
    startX + cropW > w ||
    startY + cropH > h
  ) {
    return sourceCanvas;
  }
  const canvas = document.createElement('canvas');
  canvas.width = cropW;
  canvas.height = cropH;
  const context = canvas.getContext('2d');
  if (!context) return sourceCanvas;
  context.drawImage(
    sourceCanvas,
    startX,
    startY,
    cropW,
    cropH,
    0,
    0,
    cropW,
    cropH
  );
  return canvas;
}

async function applyAspectRatioCrop(
  sourceCanvas: HTMLCanvasElement,
  aspectRatio: number,
  useSmartCrop: boolean | undefined
): Promise<HTMLCanvasElement> {
  const width = sourceCanvas.width;
  const height = sourceCanvas.height;

  let startX: number;
  let startY: number;
  let cropWidth: number;
  let cropHeight: number;

  if (useSmartCrop) {
    const dataUrl = sourceCanvas.toDataURL('image/png');
    const image = new Image();

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('圖片載入失敗'));
      image.src = dataUrl;
    });

    const [, , regionWidth, regionHeight] = getCropRegion(
      width,
      height,
      aspectRatio
    );
    const smartcropModule = await import('smartcrop');
    const moduleWithDefault = smartcropModule as {
      default?: {
        crop: (
          img: HTMLImageElement,
          options: { width: number; height: number }
        ) => Promise<{
          topCrop: {
            x: number;
            y: number;
            width: number;
            height: number;
          };
        }>;
      };
    };

    const cropFunction = moduleWithDefault.default?.crop;
    if (!cropFunction) {
      throw new Error('smartcrop 無法使用');
    }

    const result = await cropFunction(image, {
      width: Math.round(regionWidth),
      height: Math.round(regionHeight),
    });

    const topCrop = result.topCrop;
    startX = topCrop.x;
    startY = topCrop.y;
    cropWidth = topCrop.width;
    cropHeight = topCrop.height;
  } else {
    [startX, startY, cropWidth, cropHeight] = getCropRegion(
      width,
      height,
      aspectRatio
    );
  }

  const canvas = document.createElement('canvas');
  canvas.width = cropWidth;
  canvas.height = cropHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('無法建立畫布');
  }

  context.drawImage(
    sourceCanvas,
    startX,
    startY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight
  );

  return canvas;
}

/**
 * 提供前端預覽用的長寬比裁切：會先用傳入的 HTMLImageElement 建立畫布，
 * 再套用與正式 pipeline 相同的裁切與智能裁切邏輯，回傳裁切後的 canvas。
 */
export async function getAspectRatioCropPreview(
  image: HTMLImageElement,
  aspectRatio: number,
  useSmartCrop: boolean | undefined
): Promise<HTMLCanvasElement | null> {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height || !Number.isFinite(aspectRatio) || aspectRatio <= 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(image, 0, 0, width, height);
  return applyAspectRatioCrop(canvas, aspectRatio, useSmartCrop);
}

async function applyUpscale(
  sourceCanvas: HTMLCanvasElement,
  upscaleFactor: 1 | 2 | 4
): Promise<HTMLCanvasElement> {
  if (upscaleFactor === 1) {
    return sourceCanvas;
  }

  const Upscaler = (await import('upscaler')).default;
  const upscaler = new Upscaler();

  const steps = upscaleFactor === 4 ? 2 : 1;
  let currentCanvas = sourceCanvas;

  for (let index = 0; index < steps; index += 1) {
    const temporaryBlob: Blob = await new Promise((resolve, reject) => {
      currentCanvas.toBlob(blob => {
        if (!blob) {
          reject(new Error('toBlob 失敗'));
          return;
        }
        resolve(blob);
      });
    });

    const url = URL.createObjectURL(temporaryBlob);

    try {
      const upscaledSource = await upscaler.upscale(url);
      const response = await fetch(upscaledSource);
      const blob = await response.blob();
      currentCanvas = await loadBlobToCanvas(blob);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  return currentCanvas;
}

/**
 * 套用邊線處理：導圓角／形狀遮罩／邊線。在 filters 之後呼叫。
 */
function applyBorderToCanvas(
  sourceCanvas: HTMLCanvasElement,
  options: BorderOptions
): HTMLCanvasElement {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (!w || !h) return sourceCanvas;

  const { shape, cornerRadius, borderWidth, borderColor } = options;
  const noEffect =
    shape === 'rect' &&
    cornerRadius <= 0 &&
    (borderWidth <= 0 || !borderColor.trim());
  if (noEffect) return sourceCanvas;

  const pad = Math.max(0, Math.floor(borderWidth));
  const outW = w + pad * 2;
  const outH = h + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return sourceCanvas;

  const x = pad;
  const y = pad;

  const drawPath = (): void => {
    if (shape === 'circle') {
      const cx = x + w / 2;
      const cy = y + h / 2;
      const radius = Math.min(w, h) / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.closePath();
    } else {
      const r = shape === 'rounded' && cornerRadius > 0 ? cornerRadius : 0;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, r);
      ctx.closePath();
    }
  };

  drawPath();
  ctx.save();
  ctx.clip();
  ctx.drawImage(sourceCanvas, 0, 0, w, h, x, y, w, h);
  ctx.restore();

  if (pad > 0 && borderColor.trim()) {
    drawPath();
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = pad * 2;
    ctx.stroke();
  }

  return canvas;
}

/**
 * 使用 Canvas 2D 在畫布上繪製多個文字塊／浮水印，確保輸出與預覽一致、文字不遺失。
 */
function applyWatermarkToCanvas(
  sourceCanvas: HTMLCanvasElement,
  options: WatermarkOptions
): HTMLCanvasElement {
  const items = options.items.filter(
    (item): item is WatermarkItem => !!item.text?.trim()
  );
  if (items.length === 0) return sourceCanvas;

  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (!w || !h) return sourceCanvas;

  const pad = Math.max(0, options.padding);
  const outCanvas = document.createElement('canvas');
  outCanvas.width = w;
  outCanvas.height = h;
  const ctx = outCanvas.getContext('2d');
  if (!ctx) return sourceCanvas;
  ctx.drawImage(sourceCanvas, 0, 0);

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const text = item.text.trim();
    if (!text) continue;
    const fontSize = clampWatermarkFontSizeToCanvas(item.fontSize, w, h);
    const opacity = Math.min(1, Math.max(0, item.opacity));
    const color = item.color?.trim() || '#000000';
    const fontFamily = item.fontFamily?.trim() || 'sans-serif';
    const ax = Math.min(1, Math.max(0, item.anchorX ?? 0.5));
    const ay = Math.min(1, Math.max(0, item.anchorY ?? 0.5));
    const leftPx = Math.round(Math.min(w - pad, Math.max(pad, w * ax)));
    const topPx = Math.round(Math.min(h - pad, Math.max(pad, h * ay)));

    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.font = `${fontSize}px ${fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, leftPx, topPx);
    ctx.restore();
  }

  return outCanvas;
}

/**
 * 在畫布上繪製圖示層（Lucide／Simple Icons／上傳圖片），依 anchor 置中貼上。
 */
async function applyIconsToCanvas(
  sourceCanvas: HTMLCanvasElement,
  options: IconsOptions
): Promise<HTMLCanvasElement> {
  const items = options.items.filter(
    (item): item is IconItem =>
      !!item &&
      (((item.source === 'lucide' || item.source === 'simple') &&
        !!item.iconId) ||
        (item.source === 'custom' && !!item.imageDataUrl) ||
        (item.source === 'doodle' && !!item.iconId))
  );
  if (items.length === 0) return sourceCanvas;

  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (!w || !h) return sourceCanvas;

  const pad = Math.max(0, options.padding);
  const { getIconAsImage, getDoodleAsImage } = await import('./iconRegistry');

  const outCanvas = document.createElement('canvas');
  outCanvas.width = w;
  outCanvas.height = h;
  const ctx = outCanvas.getContext('2d');
  if (!ctx) return sourceCanvas;
  ctx.drawImage(sourceCanvas, 0, 0);

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const size = clampIconQrSizeToCanvas(item.size, w, h);
    const opacity = Math.min(1, Math.max(0, item.opacity));
    const ax = Math.min(1, Math.max(0, item.anchorX ?? 0.5));
    const ay = Math.min(1, Math.max(0, item.anchorY ?? 0.5));
    const leftPx = Math.round(
      Math.min(w - pad - size / 2, Math.max(pad - size / 2, w * ax - size / 2))
    );
    const topPx = Math.round(
      Math.min(h - pad - size / 2, Math.max(pad - size / 2, h * ay - size / 2))
    );

    try {
      let img: HTMLImageElement;
      if (item.source === 'custom' && item.imageDataUrl) {
        img = await loadImageFromDataUrl(item.imageDataUrl);
      } else if (item.source === 'doodle' && item.iconId) {
        const primaryColor = item.color?.trim();
        img = await getDoodleAsImage(
          item.iconId,
          size,
          primaryColor || undefined
        );
      } else if (
        item.iconId &&
        (item.source === 'lucide' || item.source === 'simple')
      ) {
        const color = item.color?.trim() || '#000000';
        img = await getIconAsImage(item.source, item.iconId, color, size);
      } else {
        continue;
      }
      ctx.save();
      ctx.globalAlpha = opacity;
      const iconShape = item.shape ?? 'none';
      if (iconShape === 'circle' || iconShape === 'rounded') {
        ctx.beginPath();
        if (iconShape === 'circle') {
          const cx = leftPx + size / 2;
          const cy = topPx + size / 2;
          ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
        } else {
          const r = Math.max(0, size * 0.2);
          ctx.roundRect(leftPx, topPx, size, size, r);
        }
        ctx.closePath();
        ctx.clip();
      }
      ctx.drawImage(img, leftPx, topPx, size, size);
      ctx.restore();
    } catch {
      // 單一圖示載入失敗時略過，不中斷整段
    }
  }

  return outCanvas;
}

function loadImageFromDataUrl(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Custom icon image load failed'));
    img.src = dataUrl;
  });
}

/**
 * 在畫布上繪製多個 QR Code，依各項 anchor 置中貼上。
 */
async function applyQRCodesToCanvas(
  sourceCanvas: HTMLCanvasElement,
  options: QRCodeOptions
): Promise<HTMLCanvasElement> {
  const items = (options.items ?? []).filter(
    (item): item is QRCodeItem => !!item?.content?.trim()
  );
  if (items.length === 0) return sourceCanvas;

  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (!w || !h) return sourceCanvas;

  const QRCode = await import('qrcode').then(m => m.default);
  let canvas = sourceCanvas;

  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    const content = item.content.trim();
    if (!content) continue;

    const size = clampIconQrSizeToCanvas(item.size ?? 80, w, h);
    const ax = Math.min(1, Math.max(0, item.anchorX ?? 0.5));
    const ay = Math.min(1, Math.max(0, item.anchorY ?? 0.5));
    const leftPx = Math.round(w * ax - size / 2);
    const topPx = Math.round(h * ay - size / 2);

    const dark = (item.darkColor ?? '#000000').trim() || '#000000';
    const light = (item.lightColor ?? '#ffffff').trim() || '#ffffff';
    const dataUrl = await QRCode.toDataURL(content, {
      width: size,
      margin: 1,
      color: { dark, light },
    });

    canvas = await new Promise<HTMLCanvasElement>((resolve, _reject) => {
      const img = new window.Image();
      img.onload = () => {
        const out = document.createElement('canvas');
        out.width = w;
        out.height = h;
        const ctx = out.getContext('2d');
        if (!ctx) {
          resolve(canvas);
          return;
        }
        ctx.drawImage(canvas, 0, 0);
        ctx.drawImage(img, leftPx, topPx, size, size);
        resolve(out);
      };
      img.onerror = () => resolve(canvas);
      img.src = dataUrl;
    });
  }

  return canvas;
}

export async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality?: number
): Promise<Blob> {
  return new Promise<Blob>((resolve, _reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          _reject(new Error('輸出失敗'));
          return;
        }
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

/**
 * 將圖片檔依序套用：去背 → 長寬比裁切 → AI 放大 → 亮度/對比/飽和 → 輸出尺寸 → 格式輸出。
 *
 * 進度回報以 PipelineStepId 表示，UI 可用來顯示進度條或清單。
 */
export async function runImagePipeline(
  file: File,
  options: PipelineOptions,
  onProgress?: PipelineProgressListener
): Promise<PipelineResult> {
  const completedSteps: PipelineStepId[] = [];

  const updateProgress = (currentStep: PipelineStepId | null): void => {
    if (!onProgress) return;
    onProgress({
      currentStep,
      completedSteps: [...completedSteps],
    });
  };

  updateProgress('removeBackground');

  let workingBlob: Blob;

  if (options.enableRemoveBackground) {
    const inputBlob = isPngOrJpeg(file) ? file : await toPngBlob(file);
    workingBlob = await removeBackgroundFromBlob(
      inputBlob,
      options.removeBackgroundOptions
    );
    completedSteps.push('removeBackground');
  } else {
    workingBlob = isPngOrJpeg(file) ? file : await toPngBlob(file);
  }

  updateProgress('aspectRatio');

  let canvas = await loadBlobToCanvas(workingBlob);
  let width = canvas.width;
  let height = canvas.height;

  if (options.cropRect) {
    canvas = applyNormalizedRectCrop(canvas, options.cropRect);
    width = canvas.width;
    height = canvas.height;
    completedSteps.push('aspectRatio');
  } else if (options.aspectRatio && options.aspectRatio > 0) {
    canvas = await applyAspectRatioCrop(
      canvas,
      options.aspectRatio,
      options.useSmartCrop
    );
    width = canvas.width;
    height = canvas.height;
    completedSteps.push('aspectRatio');
  }

  if (options.enableUpscale && options.upscaleFactor > 1) {
    updateProgress('upscale');
    canvas = await applyUpscale(canvas, options.upscaleFactor);
    width = canvas.width;
    height = canvas.height;
    completedSteps.push('upscale');
  }

  if (options.adjustments) {
    updateProgress('filters');
    applyAdjustmentsToCanvas(canvas, options.adjustments);
    completedSteps.push('filters');
  }

  if (options.border) {
    updateProgress('border');
    canvas = applyBorderToCanvas(canvas, options.border);
    width = canvas.width;
    height = canvas.height;
    completedSteps.push('border');
  }

  const outputWidth = options.outputSize?.width ?? 0;
  const outputHeight = options.outputSize?.height ?? 0;

  const useTargetPixels =
    options.targetOutputPixels &&
    options.targetOutputPixels.width >= MIN_PX &&
    options.targetOutputPixels.height >= MIN_PX;

  let targetWidth: number;
  let targetHeight: number;
  const scaleRatio = Math.min(1, Math.max(0.1, options.outputScaleRatio ?? 1));

  /** 手動輸入時：輸出畫布即目標 px；比例時：畫布 × ratio */
  let outputCanvasWidth: number;
  let outputCanvasHeight: number;
  if (useTargetPixels) {
    targetWidth = Math.min(
      MAX_PX,
      Math.max(MIN_PX, Math.round(options.targetOutputPixels!.width))
    );
    targetHeight = Math.min(
      MAX_PX,
      Math.max(MIN_PX, Math.round(options.targetOutputPixels!.height))
    );
    outputCanvasWidth = targetWidth;
    outputCanvasHeight = targetHeight;
  } else {
    const [tw, th] = getOutputSize(width, height, outputWidth, outputHeight);
    targetWidth = Math.round(tw * scaleRatio);
    targetHeight = Math.round(th * scaleRatio);
    outputCanvasWidth =
      outputWidth > 0 ? Math.round(outputWidth * scaleRatio) : targetWidth;
    outputCanvasHeight =
      outputHeight > 0 ? Math.round(outputHeight * scaleRatio) : targetHeight;
  }

  if (targetWidth < MIN_PX || targetHeight < MIN_PX) {
    throw new Error(`輸出尺寸請至少 ${MIN_PX} px`);
  }

  if (targetWidth > MAX_PX || targetHeight > MAX_PX) {
    throw new Error(`輸出尺寸請不超過 ${MAX_PX} px`);
  }

  const allowPadding = options.outputSize?.allowPadding === true;
  const shouldAvoidUpscale = allowPadding;

  /** 手動輸入時：圖片先 fit 進目標畫布（維持比例），再繪製到輸出畫布，與預覽一致 */
  const [imageTargetW, imageTargetH] = useTargetPixels
    ? getOutputSize(width, height, outputCanvasWidth, outputCanvasHeight)
    : [targetWidth, targetHeight];

  const willUpscale = imageTargetW > width || imageTargetH > height;
  const shouldResizeImage =
    (imageTargetW !== width || imageTargetH !== height) &&
    (!shouldAvoidUpscale || !willUpscale);

  let resizedCanvas = canvas;
  let resizedWidth = width;
  let resizedHeight = height;

  if (shouldResizeImage) {
    updateProgress('resize');
    resizedCanvas = await resizeCanvas(canvas, {
      width: imageTargetW,
      height: imageTargetH,
      highQuality: true,
    });
    resizedWidth = resizedCanvas.width;
    resizedHeight = resizedCanvas.height;
    completedSteps.push('resize');
  }

  /** 建立輸出畫布（目標尺寸）並將圖片依 anchor 繪製，與比例模式一致。有設定背景色時一律走此路徑，使輸出與預覽一致。 */
  const needPadding = useTargetPixels
    ? resizedWidth < outputCanvasWidth || resizedHeight < outputCanvasHeight
    : outputWidth > resizedWidth || outputHeight > resizedHeight;
  const paddedCanvasW = useTargetPixels
    ? outputCanvasWidth
    : outputWidth > 0
      ? Math.round(outputWidth * scaleRatio)
      : resizedWidth;
  const paddedCanvasH = useTargetPixels
    ? outputCanvasHeight
    : outputHeight > 0
      ? Math.round(outputHeight * scaleRatio)
      : resizedHeight;
  const hasBackground =
    !!options.outputSize?.backgroundColor ||
    !!options.outputSize?.backgroundImage;
  const wantBackgroundFill =
    allowPadding &&
    hasBackground &&
    outputWidth > 0 &&
    outputHeight > 0 &&
    resizedWidth <= paddedCanvasW &&
    resizedHeight <= paddedCanvasH;
  if (allowPadding && (needPadding || wantBackgroundFill)) {
    const canvasWidth = paddedCanvasW;
    const canvasHeight = paddedCanvasH;

    if (canvasWidth < MIN_PX || canvasHeight < MIN_PX) {
      throw new Error(`輸出尺寸請至少 ${MIN_PX} px`);
    }

    if (canvasWidth > MAX_PX || canvasHeight > MAX_PX) {
      throw new Error(`輸出尺寸請不超過 ${MAX_PX} px`);
    }

    const paddedCanvas = document.createElement('canvas');
    paddedCanvas.width = canvasWidth;
    paddedCanvas.height = canvasHeight;

    const paddedContext = paddedCanvas.getContext('2d');
    if (!paddedContext) {
      throw new Error('無法建立畫布');
    }

    if (options.outputSize.backgroundImage) {
      try {
        const bgImg = await loadImageFromDataUrl(
          options.outputSize.backgroundImage
        );
        const scale = Math.max(
          canvasWidth / bgImg.naturalWidth,
          canvasHeight / bgImg.naturalHeight
        );
        const srcW = canvasWidth / scale;
        const srcH = canvasHeight / scale;
        const srcX = (bgImg.naturalWidth - srcW) / 2;
        const srcY = (bgImg.naturalHeight - srcH) / 2;
        paddedContext.drawImage(
          bgImg,
          srcX,
          srcY,
          srcW,
          srcH,
          0,
          0,
          canvasWidth,
          canvasHeight
        );
      } catch {
        paddedContext.fillStyle =
          options.outputSize.backgroundColor ?? '#ffffff';
        paddedContext.fillRect(0, 0, canvasWidth, canvasHeight);
      }
    } else if (options.outputSize.backgroundColor) {
      paddedContext.fillStyle = options.outputSize.backgroundColor;
      paddedContext.fillRect(0, 0, canvasWidth, canvasHeight);
    }

    const freeWidth = canvasWidth - resizedWidth;
    const freeHeight = canvasHeight - resizedHeight;
    const anchorX = options.outputSize.anchorX ?? 0.5;
    const anchorY = options.outputSize.anchorY ?? 0.5;

    const offsetX = freeWidth * Math.min(Math.max(anchorX, 0), 1);
    const offsetY = freeHeight * Math.min(Math.max(anchorY, 0), 1);

    paddedContext.drawImage(
      resizedCanvas,
      0,
      0,
      resizedWidth,
      resizedHeight,
      offsetX,
      offsetY,
      resizedWidth,
      resizedHeight
    );

    canvas = paddedCanvas;
  } else {
    canvas = resizedCanvas;
  }

  // 輸出比例／手動尺寸會縮小畫布，疊加層（圖示、文字、QR）的尺寸與 padding 需同步縮放，與畫布一致
  const designWidth = width;
  const designHeight = height;
  const overlayScale = Math.min(
    canvas.width / designWidth,
    canvas.height / designHeight
  );

  const hasIcons = (options.icons?.items?.length ?? 0) > 0;
  if (hasIcons && options.icons) {
    updateProgress('icons');
    const scaledIcons: IconsOptions = {
      padding: Math.round(options.icons.padding * overlayScale),
      items: options.icons.items.map(item => ({
        ...item,
        size: Math.max(1, Math.round(item.size * overlayScale)),
      })),
    };
    canvas = await applyIconsToCanvas(canvas, scaledIcons);
    completedSteps.push('icons');
  }

  const hasWatermark =
    options.watermark?.items?.some(item => item.text?.trim()) ?? false;
  if (hasWatermark && options.watermark) {
    updateProgress('watermark');
    const scaledWatermark: WatermarkOptions = {
      padding: Math.round(options.watermark.padding * overlayScale),
      items: options.watermark.items.map(item => ({
        ...item,
        fontSize: Math.max(1, Math.round(item.fontSize * overlayScale)),
      })),
    };
    canvas = await applyWatermarkToCanvas(canvas, scaledWatermark);
    completedSteps.push('watermark');
  }

  const qrItems = options.qrcode?.items ?? [];
  const hasQRCode = qrItems.some(
    (i: QRCodeItem) => (i.content ?? '').trim().length > 0
  );
  if (hasQRCode && options.qrcode) {
    updateProgress('qrcode');
    const scaledQrcode: QRCodeOptions = {
      items: options.qrcode.items.map(item => ({
        ...item,
        size: Math.max(1, Math.round((item.size ?? 80) * overlayScale)),
      })),
    };
    canvas = await applyQRCodesToCanvas(canvas, scaledQrcode);
    completedSteps.push('qrcode');
  }

  updateProgress('format');

  const blob = await canvasToBlob(
    canvas,
    options.format.mime,
    options.format.quality
  );
  completedSteps.push('format');

  updateProgress(null);

  return {
    blob,
    progress: {
      currentStep: null,
      completedSteps,
    },
  };
}
